import { OpenAPI } from 'openapi'
// `request` isn't re-exported from the openapi barrel, but it's the same core
// helper every generated service uses. Import it directly (read-only) so this
// hand-written call shares the identical HTTP client without editing generated
// code or duplicating fetch/auth/refresh logic.
import { request as __request } from 'openapi/core/request'

/**
 * A single approved-but-unclaimed referrer reward — the user must pick which
 * business to add the credits to before the grant lands. Identity of the
 * referee is intentionally omitted; the celebration copy is always generic
 * ("a friend you invited") to keep referees anonymous.
 */
export interface ClaimableReward {
  /** The referral row ID — passed to `POST /v1/referrals/claim` to consume it. */
  referralId: string
  /** Referrer credits waiting to be granted for this referral. */
  credits: number
}

/**
 * Referral program info for the authenticated user.
 *
 * Matches the fixed server contract for `GET /v1/referrals/me`. The client
 * OpenAPI generator hasn't emitted a model for this endpoint yet, so this type
 * is hand-written to the contract. When the generated model lands, callers can
 * switch to it without changing shape.
 */
export interface ReferralInfoDto {
  /** Whether the referral program is enabled for this user server-side. */
  enabled: boolean
  /** The user's referral code, or null when not (yet) provisioned. */
  code: string | null
  /** The full shareable link (`https://taka.ai/?ref=CODE`), or null. */
  link: string | null
  /** Friends whose referral has been rewarded (their first real charge landed). */
  rewardedCount: number
  /** Total credits the referrer has earned from rewarded referrals. */
  creditsEarned: number
  /** Friends who joined but whose reward hasn't landed yet. */
  pendingCount: number
  /** Credits the referee gets at trial start (drives the offer copy). */
  refereeCredits: number
  /** Credits the referrer gets at the friend's first paid charge. */
  referrerCredits: number
  /**
   * Approved rewards the user has not yet claimed into a business, oldest
   * first. Empty for the common case; non-empty means the celebration UI
   * should surface — one auto-claim if the user has a single business, a
   * business picker dialog otherwise.
   */
  claimable: ClaimableReward[]
  /**
   * Credits this user received as an invited friend (referee). Non-null only
   * when they signed up via a referral link and the credits have already landed.
   * Drives the one-time referee welcome celebration dialog. Null when not referred.
   */
  receivedRefereeCredits: number | null
}

/**
 * Fetch the current user's referral program info.
 *
 * Reuses the generated OpenAPI `request` helper + `OpenAPI` config so this
 * hand-written call shares the exact same HTTP client, auth (Bearer token),
 * credentials, error handling, and token-refresh behavior as every generated
 * service call. Only the endpoint typing is bespoke.
 */
export function getReferralInfo(): Promise<ReferralInfoDto> {
  return __request<ReferralInfoDto>(OpenAPI, {
    method: 'GET',
    url: '/v1/referrals/me',
  })
}

/**
 * Claim one or more approved referral rewards into a specific business. The
 * server validates that `businessId` is an active membership (else 403) and
 * treats already-claimed rows as no-ops, so the returned `granted` may be
 * lower than the request count without being an error.
 *
 * Uses the same generated `__request` helper as `getReferralInfo` so the
 * bearer token, refresh flow, and error shape are identical to every other
 * authenticated call.
 */
export function claimReferralReward(
  referralIds: string[],
  businessId: string
): Promise<{ granted: number }> {
  return __request<{ granted: number }>(OpenAPI, {
    method: 'POST',
    url: '/v1/referrals/claim',
    body: { referralIds, businessId },
  })
}
